import React from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import { TOrbit } from '../../../i18n/TModules';
import { hasNoPrizeRow, probabilityTotal } from '../adapters/game';
import { Translate, describeTrigger, transactionParts } from '../adapters/ruleAdapter';
import { totalStamps } from '../adapters/stamp';
import { FinalSlotTag, IReviewRow } from '../kit/ReviewTable';
import { WEDGE_INK } from '../types/game/WheelSteps';
import {
  WizardEditTarget,
  WizardLimits,
  WizardPrize,
  WizardState,
  WizardTrigger,
  WizardWedge
} from '../wizardModel';
import { wz } from '../wizardStyles';

const TR = `${TOrbit}.wizard.review`;
const TL = `${TOrbit}.wizard.limits`;

export const formatMoment = (value: string) =>
  moment.parseZone(value).format('D MMM YYYY HH:mm:ss (Z)');

const triggerValue = (trigger: WizardTrigger, t: Translate) => {
  const { title, detail } = describeTrigger(trigger, t);
  return detail === '' ? title : `${title}\n${detail}`;
};

export const prizeLine = (prize: WizardPrize, t: Translate) =>
  prize.type === 'points'
    ? t(`${TR}.issuePoints`, { count: prize.points, id: prize.loyalty?.id ?? '' })
    : t(`${TR}.issueReward`, { name: prize.reward?.name ?? '', id: prize.reward?.id ?? '' });

/** Games name the programme (6778:25220 / :25970): "Award 100 points from Loyalty Program 1". */
export const gamePrizeLine = (prize: WizardPrize, t: Translate) =>
  prize.type === 'points'
    ? t(`${TR}.awardPoints`, { count: prize.points, name: prize.loyalty?.name ?? '' })
    : t(`${TR}.issueReward`, { name: prize.reward?.name ?? '', id: prize.reward?.id ?? '' });

const limitText = (on: boolean, value: string, t: Translate) =>
  on && value !== '' ? value : t(`${TR}.none`);

export const scheduleRows = (state: WizardState, t: Translate, asked: boolean): IReviewRow[] => {
  const { limits } = state;
  return [
    {
      key: 'start',
      label: t(`${TR}.start`),
      value: asked && limits.start !== null ? formatMoment(limits.start) : t(`${TR}.now`)
    },
    {
      key: 'end',
      label: t(`${TR}.end`),
      value:
        asked && limits.endOn && limits.end !== null ? formatMoment(limits.end) : t(`${TR}.never`)
    }
  ];
};

export const enrolmentRow = (limits: WizardLimits, t: Translate): IReviewRow => ({
  key: 'enrolment',
  label: t(`${TR}.enrolment`),
  value: limits.autoEnrol
    ? t(`${TR}.automatic`)
    : limits.enrolByOn && limits.enrolBy !== null
    ? t(`${TR}.selfEnrolBy`, { date: formatMoment(limits.enrolBy) })
    : t(`${TR}.selfEnrol`)
});

// 6778:21061: property keys and values in italic orange.
const PropertyWord = styled.em`
  color: ${wz.highlight};
  font-size: 13px;
`;

const ConditionLine = styled.span`
  color: ${wz.secondary};
  font-size: 13px;
`;

/** 6778:21061: the trigger, then each condition on its own line, property rows after an AND. */
const triggerCell = (trigger: WizardTrigger, t: Translate): React.ReactNode => {
  if (trigger.kind !== 'transaction') {
    return triggerValue(trigger, t);
  }
  const { title } = describeTrigger(trigger, t);
  const { conditions, properties } = transactionParts(trigger, t);
  const and = t(`${TOrbit}.wizard.summary.and`);
  return (
    <>
      {title}
      {conditions.map(line => (
        <ConditionLine key={line}>{`
${line}`}</ConditionLine>
      ))}
      {properties.map(row => (
        <ConditionLine key={`${row.key}-${row.operator}-${row.value}`}>
          {`
${and} `}
          <PropertyWord>{row.key}</PropertyWord>
          {` ${row.operator} `}
          <PropertyWord>{row.value}</PropertyWord>
        </ConditionLine>
      ))}
    </>
  );
};

export const triggerRows = (state: WizardState, t: Translate, numbered: 'task' | 'rule' | null) =>
  numbered === null
    ? [{ key: 'trigger', label: t(`${TR}.trigger`), value: triggerCell(state.triggers[0], t) }]
    : state.triggers.map((trigger, index) => ({
        key: `${numbered}-${index}`,
        label: t(`${TR}.${numbered}`, { n: index + 1 }),
        value: triggerValue(trigger, t)
      }));

/** 6778:20847: the Stamp review, with the FINAL SLOT pill after the card's last stamp. */
const stampRows = (state: WizardState, t: Translate): IReviewRow[] => {
  const { limits } = state;
  const last = totalStamps(state.prizes);
  const prizes = [...state.prizes].sort((a, b) => a.slot - b.slot);
  return [
    ...scheduleRows(state, t, false),
    ...triggerRows(state, t, null),
    {
      key: 'rewards',
      label: t(`${TR}.rewards`),
      value: prizes.map((prize, index) => (
        <React.Fragment key={prize.slot}>
          {index > 0 ? '\n' : null}
          {t(`${TR}.slot`, { slot: prize.slot, prize: prizeLine(prize, t) })}
          {prize.slot === last ? <FinalSlotTag>{t(`${TR}.finalSlot`)}</FinalSlotTag> : null}
        </React.Fragment>
      ))
    },
    {
      key: 'limits',
      label: t(`${TR}.limits`),
      value: [
        t(`${TR}.maxCards`, { value: limitText(limits.totalOn, limits.total, t) }),
        t(`${TR}.maxCardsPerUser`, { value: limitText(limits.perUserOn, limits.perUser, t) }),
        t(`${TR}.maxEarns`, {
          value: limitText(limits.userRateOn, limits.userRate, t),
          period: t(`${TL}.period.${limits.userRatePeriod}`)
        })
      ].join('\n')
    },
    enrolmentRow(limits, t)
  ];
};

const prizeRows = (
  prizes: WizardPrize[],
  t: Translate,
  key = 'rewards',
  label = `${TR}.rewards`,
  options?: Record<string, unknown>
) => ({
  key,
  label: t(label, options),
  value: prizes.map(prize => prizeLine(prize, t)).join('\n')
});

/** 6778:23364: the dates, one row per task, the prizes and enrolment. */
const questRows = (state: WizardState, t: Translate): IReviewRow[] => [
  ...scheduleRows(state, t, true),
  ...triggerRows(state, t, 'task'),
  prizeRows(state.prizes, t),
  enrolmentRow(state.limits, t)
];

/** 6985:5636: the dates, one row per task with its points, the prizes by level, and enrolment. */
const progressRows = (state: WizardState, t: Translate): IReviewRow[] => [
  ...scheduleRows(state, t, true),
  ...state.triggers.map((trigger, index) => ({
    key: `rule-${index}`,
    label: t(`${TR}.rule`, { n: index + 1 }),
    value: `${triggerValue(trigger, t)}\n${t(`${TOrbit}.wizard.summary.points`, {
      value: trigger.points
    })}`
  })),
  {
    key: 'rewards',
    label: t(`${TR}.rewards`),
    value: state.levels
      .flatMap((level, index) =>
        level.prizes.map(prize =>
          t(`${TR}.levelPrize`, { n: index + 1, prize: prizeLine(prize, t) })
        )
      )
      .join('\n')
  },
  enrolmentRow(state.limits, t)
];

const countText = (on: boolean, value: string, t: Translate) =>
  on && value !== '' ? value : t(`${TR}.noLimit`);

/** 6778:25970: the game, its prize mode, the outcomes with their odds, tries, limits, enrolment. */
const wedgeLine = (wedge: WizardWedge, t: Translate) =>
  wedge.type === 'none'
    ? t(`${TOrbit}.wizard.game.wheel.noPrize`)
    : wedge.type === 'points'
    ? t(`${TR}.awardPoints`, { count: wedge.points, name: wedge.loyalty?.name ?? '' })
    : t(`${TR}.issueReward`, { name: wedge.reward?.name ?? '', id: wedge.reward?.id ?? '' });

// 6985:12976: numbered chips in the wedge colours, one line per wedge.
const WedgeChip = styled.span<{ $ink: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  border-radius: 10px;
  background: ${({ $ink }) => $ink};
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
`;

// 6985:12976 draws the wedge list as a short scroll area inside its cell.
const WedgeList = styled.div`
  max-height: 168px;
  overflow-y: auto;
  ${intelligenceScrollbar('y')}
`;

const gameTail = (state: WizardState, t: Translate): IReviewRow[] => {
  const { limits } = state;
  return [
    {
      key: 'tries',
      label: t(`${TR}.gameTries`),
      value: t(`${TR}.autoIssue`, { state: t(`${TL}.${limits.autoIssue ? 'on' : 'off'}`) })
    },
    {
      key: 'limits',
      label: t(`${TR}.limits`),
      value: [
        t(`${TR}.totalPlays`, { value: countText(limits.totalOn, limits.total, t) }),
        t(`${TR}.userPlaysCap`, {
          value: countText(limits.perUserOn, limits.perUser, t),
          cap:
            limits.userRateOn && limits.userRate !== ''
              ? t(`${TR}.capValue`, {
                  value: limits.userRate,
                  period: t(`${TL}.period.${limits.userRatePeriod}`)
                })
              : t(`${TR}.capNone`)
        })
      ].join('\n')
    },
    enrolmentRow(limits, t)
  ];
};

/** 6985:12976: the wheel's review, its wedges in place of a prize mode and odds. */
const wheelRows = (state: WizardState, t: Translate): IReviewRow[] => [
  ...scheduleRows(state, t, true),
  { key: 'game', label: t(`${TR}.gameType`), value: t(`${TOrbit}.wizard.game.spin_the_wheel`) },
  { key: 'wedges', label: t(`${TR}.wedgeCount`), value: String(state.game.wedges.length) },
  {
    key: 'wedgeOutcomes',
    label: t(`${TR}.wedgeOutcomes`),
    value: (
      <WedgeList>
        {state.game.wedges.map((wedge, index) => (
          // Wedges are their position on the wheel.
          <React.Fragment key={index}>
            {index > 0 ? '\n' : null}
            <WedgeChip $ink={WEDGE_INK[index % WEDGE_INK.length]}>{index + 1}</WedgeChip>
            {wedgeLine(wedge, t)}
          </React.Fragment>
        ))}
      </WedgeList>
    )
  },
  ...gameTail(state, t)
];

const gameRows = (state: WizardState, t: Translate): IReviewRow[] => {
  if (state.game.gameType === 'spin_the_wheel') {
    return wheelRows(state, t);
  }
  const { game, limits } = state;
  const choice = game.mode === 'choice';
  const winners = game.outcomes.map(outcome =>
    choice
      ? gamePrizeLine(outcome, t)
      : t(`${TR}.withProbability`, { prize: gamePrizeLine(outcome, t), n: outcome.probability })
  );
  const noPrize = hasNoPrizeRow(game)
    ? [t(`${TR}.noPrize`, { n: choice ? 100 - game.winProbability : game.noPrizeProbability })]
    : [];
  return [
    ...scheduleRows(state, t, true),
    {
      key: 'game',
      label: t(`${TR}.gameType`),
      value: t(`${TOrbit}.wizard.game.${game.gameType ?? 'shake_the_tree'}`)
    },
    {
      key: 'mode',
      label: t(`${TR}.prizeMode`),
      value: t(`${TOrbit}.wizard.game.${choice ? 'choice' : 'random'}`)
    },
    { key: 'outcomes', label: t(`${TR}.outcomes`), value: [...winners, ...noPrize].join('\n') },
    choice
      ? {
          key: 'odds',
          label: t(`${TOrbit}.wizard.game.win`),
          value: `${game.winProbability}%`
        }
      : { key: 'odds', label: t(`${TR}.probabilityTotal`), value: `${probabilityTotal(game)}%` },
    {
      key: 'tries',
      label: t(`${TR}.gameTries`),
      value: t(`${TR}.autoIssue`, { state: t(`${TL}.${limits.autoIssue ? 'on' : 'off'}`) })
    },
    {
      key: 'limits',
      label: t(`${TR}.limits`),
      value: [
        t(`${TR}.totalPlays`, { value: countText(limits.totalOn, limits.total, t) }),
        t(`${TR}.userPlaysCap`, {
          value: countText(limits.perUserOn, limits.perUser, t),
          cap:
            limits.userRateOn && limits.userRate !== ''
              ? t(`${TR}.capValue`, {
                  value: limits.userRate,
                  period: t(`${TL}.period.${limits.userRatePeriod}`)
                })
              : t(`${TR}.capNone`)
        })
      ].join('\n')
    },
    enrolmentRow(limits, t)
  ];
};

/** Instant Reward on the Stamp / Game review pattern (no frame). */
const instantRows = (state: WizardState, t: Translate): IReviewRow[] => {
  const { limits } = state;
  return [
    ...scheduleRows(state, t, true),
    ...triggerRows(state, t, null),
    prizeRows(state.prizes, t),
    {
      key: 'limits',
      label: t(`${TR}.limits`),
      value: [
        t(`${TR}.issuedTotal`, { value: countText(limits.totalOn, limits.total, t) }),
        t(`${TR}.issuedPerUser`, { value: countText(limits.perUserOn, limits.perUser, t) }),
        t(`${TR}.issuedRate`, {
          value: countText(limits.rateOn, limits.rate, t),
          period: t(`${TL}.period.${limits.ratePeriod}`)
        }),
        t(`${TR}.issuedUserRate`, {
          value: countText(limits.userRateOn, limits.userRate, t),
          period: t(`${TL}.period.${limits.userRatePeriod}`)
        })
      ].join('\n')
    },
    enrolmentRow(limits, t)
  ];
};

const ROWS: Record<
  NonNullable<WizardState['type']>,
  (state: WizardState, t: Translate) => IReviewRow[]
> = {
  stamp: stampRows,
  quest: questRows,
  progress: progressRows,
  game: gameRows,
  instant_outcome: instantRows
};

const SECTION_STEP: Record<string, WizardEditTarget['step']> = {
  start: 'limits',
  end: 'limits',
  rewards: 'prizes',
  limits: 'limits',
  enrolment: 'limits',
  tries: 'limits',
  game: 'game',
  mode: 'outcomes',
  outcomes: 'outcomes',
  odds: 'probability',
  wedges: 'wedges',
  wedgeOutcomes: 'wedgeOutcomes'
};

/** Where each review section's "Edit" goes back to; Stamp never asks its dates. */
const editTarget = (key: string, type: WizardState['type']): WizardEditTarget | undefined => {
  if (key === 'trigger') return { step: 'trigger', editing: 0 };
  const listed = /^(task|rule|level)-(\d+)$/.exec(key);
  if (listed !== null) {
    const index = Number(listed[2]);
    return listed[1] === 'level'
      ? { step: 'prizes', activeLevel: index }
      : { step: 'trigger', editing: index };
  }
  if (type === 'stamp' && (key === 'start' || key === 'end')) return undefined;
  const step = SECTION_STEP[key];
  return step === undefined ? undefined : { step };
};

export const reviewRows = (state: WizardState, t: Translate): IReviewRow[] =>
  ROWS[state.type ?? 'stamp'](state, t).map(row => ({
    ...row,
    edit: editTarget(row.key, state.type)
  }));
